import React, { useState, useEffect, useRef } from "react";
import Navbarsticky from "./NavbarSticky";
import { Col, Row, Form } from "react-bootstrap";
import Footer from "./Footer";
import { useDispatch } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { FaMapMarkerAlt, FaStar } from "react-icons/fa";
import { FaCheckCircle } from "react-icons/fa";
import {
  getHotelList,
  getsearchsuggestion,
  getPropertyType,
} from "../store/slices/hotel";

export default function Hotellist() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const checkInParam = searchParams.get("checkIn");
  const checkOutParam = searchParams.get("checkOut");
  const roomsParam = Number(searchParams.get("rooms")) || 1;
  const guestsParam = Number(searchParams.get("guests")) || 1;
  const idParam = searchParams.get("id");
  const nameParam = searchParams.get("name");
  const typeParam = searchParams.get("type"); // hotel | place

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);

  const dropdownRef = useRef(null);
  const firstRun = useRef(true);

  const itemsPerPage = 5;
  const [city, setCity] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [selectedOption, setSelectedOption] = useState(
    idParam && typeParam
      ? { _id: idParam, name: nameParam || "", type: typeParam }
      : null,
  );

  const [error, setError] = useState("");

  const [checkInDate, setCheckInDate] = useState(checkInParam);
  const [checkOutDate, setCheckOutDate] = useState(checkOutParam);
  const [guests, setGuests] = useState(guestsParam);

  const [rooms, setRooms] = useState([{ adults: 2, children: 0 }]);
  const [isRoomGuestOpen, setIsRoomGuestOpen] = useState(false);

  const [sortBy, setSortBy] = useState("default");
  const [priceFilter, setPriceFilter] = useState("default");
  const [propertyTypeFilter, setPropertyTypeFilter] = useState("default");
  const [starRatingFilter, setStarRatingFilter] = useState("default");
  const [propertyTypes, setPropertyTypes] = useState([]);

  const [data, setData] = useState({ hotelData: [] });
  const [isloading, setIsLoading] = useState(false);

  const [dateError, setDateError] = useState("");
  const [isDateInvalid, setIsDateInvalid] = useState(false);

  useEffect(() => {
    const fetchPropertyTypes = async () => {
      try {
        const res = await dispatch(getPropertyType()).unwrap();
        const list =
          res?.propertytype?.propertyTypeData || res?.propertytype || [];
        setPropertyTypes(list);
      } catch (err) {
        console.error("Failed to load property types", err);
      }
    };
    fetchPropertyTypes();
  }, [dispatch]);

  const parseDateToUTC = (d) => {
    if (!d) return null;
    const [y, m, day] = d.split("-").map(Number);
    return Date.UTC(y, m - 1, day);
  };

  const validateDates = () => {
    setDateError("");
    setIsDateInvalid(false);

    if (!checkInDate || !checkOutDate) return true;

    const diff =
      (parseDateToUTC(checkOutDate) - parseDateToUTC(checkInDate)) /
      (1000 * 60 * 60 * 24);

    if (diff <= 0) {
      setDateError("Select another date");
      setIsDateInvalid(true);
      return false;
    }
    return true;
  };

  const buildFilterPayload = (pageToSend = page) => {
    if (!selectedOption?._id) return null;

    const payload = {
      page: pageToSend,
      limit: itemsPerPage,
      checkin: checkInDate || undefined,
      checkout: checkOutDate || undefined,
      pax: guests,
      includeRooms: true,

      // 🔥 ALL FILTERS
      sortBy: sortBy !== "default" ? sortBy : undefined,
      price: priceFilter !== "default" ? priceFilter : undefined,
      propertyType:
        propertyTypeFilter !== "default" ? propertyTypeFilter : undefined,
      starRating: starRatingFilter !== "default" ? starRatingFilter : undefined,
    };

    if (selectedOption.type === "hotel") {
      payload.hotelId = selectedOption._id;
    } else {
      payload.place = selectedOption._id;
    }

    return payload;
  };

  const totalGuests = rooms.reduce((sum, r) => sum + r.adults + r.children, 0);

  const addRoom = () => {
    setRooms([...rooms, { adults: 2, children: 0 }]);
  };

  const removeRoom = (index) => {
    const updated = rooms.filter((_, i) => i !== index);
    setRooms(updated);
  };

  const updateRoomCount = (index, type, action) => {
    const updated = rooms.map((room, i) => {
      if (i === index) {
        return {
          ...room,
          [type]:
            action === "inc" ? room[type] + 1 : Math.max(0, room[type] - 1),
        };
      }
      return room;
    });
    setRooms(updated);
  };

  // toggle dropdown
  const toggleRoomDropdown = () => {
    setIsRoomGuestOpen(!isRoomGuestOpen);
  };

  const fetchHotels = async (payload = {}, pageToFetch = page) => {
    if (!validateDates()) return;

    setIsLoading(true);

    try {
      const finalPayload = {
        ...payload,
        page: pageToFetch,
        limit: itemsPerPage,
      };

      const res = await dispatch(getHotelList(finalPayload)).unwrap();
      const safeHotels = Array.isArray(res?.hotels) ? res.hotels : [];

      setData((prev) => ({
        hotelData:
          pageToFetch === 1
            ? safeHotels
            : [...(prev.hotelData || []), ...safeHotels],
      }));

      setHasMore(safeHotels.length === itemsPerPage);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSearchSuggestions = async (text) => {
    if (!text || text.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const res = await dispatch(
        getsearchsuggestion({ search: text }),
      ).unwrap();

      setSuggestions(res || []);
    } catch (err) {
      console.error("Search suggestion error:", err);
    }
  };

  useEffect(() => {
    if (!validateDates()) return;

    const payload = buildFilterPayload(page);
    if (!payload) return;

    fetchHotels(payload, page);
  }, [page]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownVisible(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // auto-refresh on change , skip first render
  //  auto-refresh filters (REPLACE the old one)
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }

    if (!validateDates()) return;

    setPage(1);
    setHasMore(true);
    setData({ hotelData: [] });

    const payload = buildFilterPayload(1);
    if (!payload) return;

    fetchHotels(payload, 1);
  }, [sortBy, priceFilter, propertyTypeFilter, starRatingFilter]);

  useEffect(() => {
    validateDates();
  }, [checkInDate, checkOutDate]);

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    setCity(option.name); // 🔥 ensures visible text
    setDropdownVisible(false);
    setError("");
  };
  // 🔥 Restore selected option from URL params
  useEffect(() => {
    if (idParam && nameParam && typeParam) {
      const option = {
        _id: idParam,
        name: nameParam,
        type: typeParam, // "hotel" | "place"
      };

      setSelectedOption(option);
      setCity(nameParam);
    }
  }, [idParam, nameParam, typeParam]);

  const hotelSearch = async () => {
    console.log("Search clicked", selectedOption);

    if (!selectedOption || !selectedOption._id) {
      setError("Please select a destination");
      return;
    }

    if (!validateDates()) return;

    // 🔥 RESET pagination state
    setPage(1);
    setHasMore(true);

    const payload = {
      page: 1,
      limit: itemsPerPage,
      ...(selectedOption.type === "hotel"
        ? { hotelId: selectedOption._id }
        : { place: selectedOption._id }),

      checkin: checkInDate || undefined,
      checkout: checkOutDate || undefined,
      pax: guests || 1,
    };

    console.log("FINAL PAYLOAD →", payload);

    await fetchHotels(payload);
  };

  const getStarCount = (rating) => {
    if (!rating) return 0;
    const n = Number(rating.replace(/\D/g, ""));
    return isNaN(n) ? 0 : n;
  };

  useEffect(() => {
    if (!loaderRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isloading) {
          setPage((p) => p + 1);
        }
      },
      { threshold: 0.5 }, //  1 breaks on small loaders
    );

    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, isloading]);

  const HotelSkeleton = () => (
    <div className="hotel-card skeleton">
      <div className="skeleton-img" />
      <div className="skeleton-content">
        <div className="skeleton-line w-60" />
        <div className="skeleton-line w-40" />
        <div className="skeleton-line w-80" />
      </div>
    </div>
  );

  return (
    <>
      <Navbarsticky />

      <div className="hotellist-container "></div>

      <div className="container mt-2">
        <div className="hotellist-input">
          <div className="hotellist-search-wrapper">
            <div className="hotellist-form filter-box shadow-sm">
              <Form.Group className="mb-3 mx-1" ref={dropdownRef}>
                <Form.Label className="filter-label">
                  Location or Hotel Name
                </Form.Label>

                <div className="input-with-icon">
                  <input
                    type="text"
                    className="filter-input"
                    value={
                      dropdownVisible ? city : selectedOption?.name || city
                    }
                    placeholder="Location or Hotel Name"
                    onChange={(e) => {
                      const value = e.target.value;
                      setCity(value);
                      setSelectedOption(null);
                      setDropdownVisible(true);
                      setError("");
                      fetchSearchSuggestions(value);
                    }}
                    onClick={() => setDropdownVisible(true)}
                  />

                  <i className="bi bi-geo-alt" />
                </div>

                {dropdownVisible && (
                  <ul
                    style={{
                      listStyleType: "none",
                      padding: 0,
                      margin: "5px 0",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      maxHeight: "200px",
                      overflowY: "auto",
                      position: "absolute",
                      width: "260px",
                      backgroundColor: "#fff",
                      zIndex: 1000,
                    }}
                  >
                    {selectedOption && (
                      <>
                        <li
                          style={{
                            padding: "5px",
                            fontWeight: "bold",
                            backgroundColor: "#f0f0f0",
                            borderBottom: "1px solid #ccc",
                          }}
                        >
                          Recently Selected
                        </li>
                        <li
                          onClick={() => handleOptionClick(selectedOption)}
                          style={{ padding: "5px", cursor: "pointer" }}
                        >
                          {selectedOption.name}
                        </li>
                      </>
                    )}

                    {suggestions.map((option) => (
                      <li
                        key={option.id}
                        onClick={() =>
                          handleOptionClick({
                            _id: option.id,
                            name: option.label,
                            type: option.type, // 🔥 REQUIRED
                          })
                        }
                        style={{
                          padding: "6px 8px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        {option.type === "place" ? "📍" : "🏨"} {option.label}
                      </li>
                    ))}
                  </ul>
                )}

                {error && (
                  <p
                    style={{ color: "red", marginTop: "5px", fontSize: "12px" }}
                  >
                    {error}
                  </p>
                )}
              </Form.Group>

              {/* checkin */}
              <Form.Group className="mb-3 mx-1">
                <Form.Label className="filter-label">Check In</Form.Label>
                <div className="input-with-icon">
                  <input
                    type="date"
                    className="filter-input"
                    value={checkInDate}
                    onChange={(e) => setCheckInDate(e.target.value)}
                  />
                  <i className="bi bi-calendar-date" />
                </div>
              </Form.Group>

              {/* Checkout */}
              <Form.Group className="mb-3 mx-1">
                <Form.Label className="filter-label">Check Out</Form.Label>
                <div className="input-with-icon">
                  <input
                    type="date"
                    className="filter-input"
                    value={checkOutDate}
                    onChange={(e) => setCheckOutDate(e.target.value)}
                  />
                  <i className="bi bi-calendar-date" />
                </div>
                {dateError && (
                  <p
                    style={{ color: "red", marginTop: "5px", fontSize: "12px" }}
                  >
                    {dateError}
                  </p>
                )}
              </Form.Group>

              <Form.Group className="mb-3 mx-1 roomguest-wrapper">
                <Form.Label className="filter-label">Room / Guest</Form.Label>

                <div className="roomguest-input" onClick={toggleRoomDropdown}>
                  <input
                    readOnly
                    className="filter-input"
                    value={`${rooms.length} Room${
                      rooms.length > 1 ? "s" : ""
                    }, ${totalGuests} Guest${totalGuests > 1 ? "s" : ""}`}
                  />
                  <i className="bi bi-people roomguest-icon"></i>
                </div>

                {isRoomGuestOpen && (
                  <div className="roomguest-dropdown">
                    {rooms.map((room, index) => (
                      <div className="roomguest-box" key={index}>
                        <h6 className="roomguest-title">Room {index + 1}</h6>

                        <div className="roomguest-row">
                          <span>Adult (Above 12yrs)</span>
                          <div className="roomguest-counter">
                            <button
                              onClick={() =>
                                updateRoomCount(index, "adults", "dec")
                              }
                            >
                              -
                            </button>
                            <span>{room.adults}</span>
                            <button
                              onClick={() =>
                                updateRoomCount(index, "adults", "inc")
                              }
                            >
                              +
                            </button>
                          </div>
                        </div>

                        <div className="roomguest-row">
                          <span>Child (Below 12yrs)</span>
                          <div className="roomguest-counter">
                            <button
                              onClick={() =>
                                updateRoomCount(index, "children", "dec")
                              }
                            >
                              -
                            </button>
                            <span>{room.children}</span>
                            <button
                              onClick={() =>
                                updateRoomCount(index, "children", "inc")
                              }
                            >
                              +
                            </button>
                          </div>
                        </div>

                        {rooms.length > 1 && (
                          <button
                            className="roomguest-remove"
                            onClick={() => removeRoom(index)}
                          >
                            Remove Room
                          </button>
                        )}
                      </div>
                    ))}

                    <div className="roomguest-footer">
                      <button className="roomguest-add" onClick={addRoom}>
                        + Add Room
                      </button>
                      <button
                        className="roomguest-done"
                        onClick={toggleRoomDropdown}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-3 mx-2 mt-2">
                <Form.Label className="filter-label">&nbsp;</Form.Label>

                <button
                  type="button" // 🔥 NOT submit
                  className="take-tour-btn mt-4"
                  onClick={hotelSearch}
                >
                  Search <i className="bi bi-arrow-right ms-2" />
                </button>
              </Form.Group>
            </div>

            <div className="row g-3 align-items-end  hotellist-filters">
              {/* Sort By */}
              <div className="col-6 col-md-3 col-lg-3">
                <select
                  className="form-select search-select"
                  name="sortBy"
                  aria-label="Sort By"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="default">Sort By</option>
                  <option value="hight-to-low">Price - High To Low</option>
                  <option value="low-to-high">Price - Low To High</option>
                </select>
              </div>

              {/* Price */}
              <div className="col-6 col-md-3 col-lg-3">
                <select
                  className="form-select search-select"
                  name="price"
                  aria-label="Price"
                  value={priceFilter}
                  onChange={(e) => setPriceFilter(e.target.value)}
                >
                  <option value="default">Price</option>
                  <option value="Below 1500">Below 1500</option>
                  <option value="1500-2500">₹1500 - ₹2500</option>
                  <option value="2500-5000">₹2500 - ₹5000</option>
                  <option value="5000-8000">₹5000 - ₹8000</option>
                  <option value="8000-15000">₹8000 - ₹15000</option>
                  <option value="15000-25000">₹15000 - ₹25000</option>
                  <option value="Above 25000">Above ₹25000</option>
                </select>
              </div>

              {/* Property Type */}
              <div className="col-6 col-md-3 col-lg-3">
                <select
                  className="form-select search-select"
                  name="propertyType"
                  aria-label="Property Type"
                  value={propertyTypeFilter}
                  onChange={(e) => setPropertyTypeFilter(e.target.value)}
                >
                  <option value="default">All Property</option>
                  {propertyTypes.map((pt) => (
                    <option key={pt._id} value={pt._id}>
                      {pt.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Star Rating */}
              <div className="col-6 col-md-3 col-lg-3">
                <select
                  className="form-select search-select"
                  name="starRating"
                  aria-label="Star Rating"
                  value={starRatingFilter}
                  onChange={(e) => setStarRatingFilter(e.target.value)}
                >
                  <option value="default">Star Rating</option>
                  <option value="5 Star">5 Star</option>
                  <option value="4 Star">4 Star</option>
                  <option value="3 Star">3 Star</option>
                  <option value="2 Star">2 Star</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="hotellist-card">
          <Col>
            <div className="container py-4 mx-1 my-1">
              {isDateInvalid ? null : (
                <>
                  {/* HOTEL CARDS */}
                  {data?.hotelData?.map((item) => (
                    <Row key={item._id} className="col-md-9 mx-auto">
                      <Row className="hotel-card g-3">
                        <Col md={4}>
                          <div className="hotel-img-wrapper">
                            <img
                              src={item.images?.[0]?.mediaLink}
                              alt={item.name}
                              className="hotel-img"
                            />
                            <span className="hotel-badge">
                              {item.starRating}
                            </span>
                          </div>
                        </Col>

                        <Col md={8}>
                          <h5>{item.name}</h5>

                          <div className="hotel-location">
                            <FaMapMarkerAlt /> {item.locality?.name}
                          </div>

                          <div className="hotel-stars">
                            {Array.from({
                              length: getStarCount(item.starRating),
                            }).map((_, i) => (
                              <FaStar key={i} />
                            ))}
                            <span className="star-label">
                              {getStarCount(item.starRating)}-Star Property
                            </span>
                          </div>

                          <div className="hotel-amenities">
                            {item.amenities?.slice(0, 6).map((a) => (
                              <div key={a._id} className="amenity-item">
                                <FaCheckCircle className="amenity-tick" />
                                <span>{a.name}</span>
                              </div>
                            ))}
                          </div>

                          <div className="hotel-footer">
                            <div>
                              <div className="hotel-price">
                                ₹{item.standardPrice}
                              </div>
                              <small className="price-note">per night</small>
                            </div>

                            <Link
                              to={`/hoteldetails/${item._id}`}
                              className="hotel-btn"
                              target="_blank"
                            >
                              Check Availability
                            </Link>
                          </div>
                        </Col>
                      </Row>
                    </Row>
                  ))}

                  {/* OBSERVER TARGET */}
                  <div
                    ref={loaderRef}
                    style={{
                      height: "20px",
                    }}
                  />

                  {/* SKELETONS */}
                  {isloading &&
                    Array.from({ length: 3 }).map((_, i) => (
                      <Row key={`sk-${i}`} className="col-md-9 mx-auto">
                        <HotelSkeleton />
                      </Row>
                    ))}

                  {!isloading && data?.hotelData?.length === 0 && (
                    <p className="text-center">No hotels found</p>
                  )}
                </>
              )}
            </div>
          </Col>
        </div>
      </div>
      <Footer />
    </>
  );
}
